import { useState } from 'react';
import { api, ago, useApi, type ApiFailure } from '../lib/api.ts';
import { ErrorState, Loading, Sheet, Status, orgPath, useOrg, useToast } from '../lib/ui.tsx';

const ROLES = ['owner', 'admin', 'regional_manager', 'location_manager', 'designer', 'publisher', 'advertiser', 'analyst'] as const;
const LOCATION_ROLES = ['regional_manager', 'location_manager'];
const label = (r: string) => r.replace('_', ' ').replace(/^./, (c) => c.toUpperCase());

interface Member { id: string; email: string | null; name: string | null; role: string; status: string; locationIds: string[]; lastSignInAt: string | null; isYou: boolean }
interface Invitation { id: string; email: string; role: string; expiresAt: string }

/** Team members and invitations (members.manage). People join by signing in with the invited email. */
export function Team() {
  const org = useOrg();
  const toast = useToast();
  const team = useApi<{ members: Member[]; invitations: Invitation[] }>(orgPath(org, '/members'), [org.id]);
  const locations = useApi<{ locations: { id: string; name: string }[] }>(orgPath(org, '/locations'), [org.id]);
  const advertisers = useApi<{ advertisers: { id: string; name: string }[] }>(orgPath(org, '/advertisers'), [org.id]);
  const [form, setForm] = useState<{ email: string; role: string; locationIds: string[]; advertiserId: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const roles = ROLES.filter((r) => r !== 'owner' || org.role === 'owner');

  async function run(fn: () => Promise<unknown>, done: string) {
    setBusy(true);
    try {
      await fn();
      toast(done);
      void team.reload();
      return true;
    } catch (e) {
      toast((e as ApiFailure).message, 'danger');
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function invite() {
    if (!form) return;
    const ok = await run(
      () => api('POST', orgPath(org, '/invitations'), {
        email: form.email,
        role: form.role,
        ...(LOCATION_ROLES.includes(form.role) ? { locationIds: form.locationIds } : {}),
        ...(form.role === 'advertiser' ? { advertiserId: form.advertiserId || null } : {}),
      }),
      `Invitation saved. ${form.email} gets access the first time they sign in.`,
    );
    if (ok) setForm(null);
  }

  const changeRole = (m: Member, role: string) => {
    if (LOCATION_ROLES.includes(role) && !m.locationIds.length) {
      toast('Location roles need locations. Invite again with the locations, or pick another role.', 'danger');
      return;
    }
    void run(() => api('PATCH', orgPath(org, `/members/${m.id}`), { role }), `${m.email ?? 'Member'} is now ${label(role).toLowerCase()}`);
  };

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-head">
        <h2>Team</h2>
        <button className="btn btn-sm btn-primary" onClick={() => setForm({ email: '', role: 'designer', locationIds: [], advertiserId: '' })}>Invite someone</button>
      </div>
      {team.error && <div className="card-pad"><ErrorState error={team.error} retry={team.reload} /></div>}
      {!team.data ? <div className="card-pad"><Loading rows={2} /></div> : (
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Person</th><th>Role</th><th>Last sign-in</th><th><span className="sr-only">Actions</span></th></tr></thead>
            <tbody>
              {team.data.members.map((m) => (
                <tr key={m.id}>
                  <td>{m.name ?? m.email ?? 'Unknown'}{m.isYou && <span className="subtle"> (you)</span>}<div className="subtle">{m.name ? m.email : null}</div></td>
                  <td>
                    {m.isYou || (m.role === 'owner' && org.role !== 'owner') ? label(m.role) : (
                      <select className="input" aria-label={`Role for ${m.email ?? m.name}`} value={m.role} disabled={busy} onChange={(e) => changeRole(m, e.target.value)}>
                        {roles.concat(roles.includes(m.role as never) ? [] : [m.role as never]).map((r) => <option key={r} value={r}>{label(r)}</option>)}
                      </select>
                    )}
                    {m.locationIds.length > 0 && <div className="subtle">{m.locationIds.length} location(s)</div>}
                  </td>
                  <td className="subtle">{m.status === 'disabled' ? <Status value="offline" label="Disabled" /> : ago(m.lastSignInAt)}</td>
                  <td>
                    {!m.isYou && !(m.role === 'owner' && org.role !== 'owner') && (
                      <button className={`btn btn-sm ${m.status === 'active' ? 'btn-danger' : ''}`} disabled={busy}
                        onClick={() => void run(() => api('PATCH', orgPath(org, `/members/${m.id}`), { status: m.status === 'active' ? 'disabled' : 'active' }), m.status === 'active' ? 'Access removed' : 'Access restored')}>
                        {m.status === 'active' ? 'Remove access' : 'Restore access'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {team.data.invitations.map((i) => (
                <tr key={i.id}>
                  <td>{i.email}<div className="subtle">Invited · joins on first sign-in</div></td>
                  <td>{label(i.role)}</td>
                  <td className="subtle">Expires {new Date(i.expiresAt).toLocaleDateString()}</td>
                  <td><button className="btn btn-sm" disabled={busy} onClick={() => void run(() => api('DELETE', orgPath(org, `/invitations/${i.id}`)), 'Invitation canceled')}>Cancel invitation</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {form && (
        <Sheet
          title="Invite someone"
          subtitle="They sign in with this email through your organization’s sign-in provider. No email is sent; tell them to open LumaCast and sign in."
          onClose={() => setForm(null)}
          footer={<button className="btn btn-primary" disabled={busy || !/^\S+@\S+\.\S+$/.test(form.email) || (LOCATION_ROLES.includes(form.role) && !form.locationIds.length) || (form.role === 'advertiser' && !form.advertiserId)} onClick={() => void invite()}>Save invitation</button>}
        >
          <div className="stack">
            <div className="field"><label htmlFor="inv-email">Work email</label><input id="inv-email" className="input" type="email" autoComplete="off" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} autoFocus /></div>
            <div className="field">
              <label htmlFor="inv-role">Role</label>
              <select id="inv-role" className="input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                {roles.map((r) => <option key={r} value={r}>{label(r)}</option>)}
              </select>
              {(form.role === 'owner' || form.role === 'admin') && <span className="subtle">Owners and admins must sign in with two-step verification.</span>}
            </div>
            {LOCATION_ROLES.includes(form.role) && (
              <fieldset className="field">
                <legend>Locations</legend>
                {locations.data?.locations.map((l) => (
                  <label key={l.id} className="row">
                    <input type="checkbox" checked={form.locationIds.includes(l.id)} onChange={(e) => setForm({ ...form, locationIds: e.target.checked ? [...form.locationIds, l.id] : form.locationIds.filter((x) => x !== l.id) })} /> {l.name}
                  </label>
                ))}
              </fieldset>
            )}
            {form.role === 'advertiser' && (
              <div className="field"><label htmlFor="inv-adv">Advertiser</label>
                <select id="inv-adv" className="input" value={form.advertiserId} onChange={(e) => setForm({ ...form, advertiserId: e.target.value })}>
                  <option value="">Choose…</option>
                  {advertisers.data?.advertisers.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
            )}
          </div>
        </Sheet>
      )}
    </div>
  );
}
